import React, { useEffect, useRef, useState } from "react";
import { Activity, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Database } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Portfolio-level meter-sync health badge for Portfolio Production.
 *
 * Summarises how many monitored sites received HH / 15-min data every day this
 * week vs how many have sync gaps — e.g. "3 sites with gaps this week". Expand
 * to see the offending sites (missing-day count + last-synced date); click a
 * site to jump into its per-site detail where the Meter-sync calendar lives.
 * Admin / manager also get a one-click "Backfill all gaps" action that fills
 * the last 30 days of missing data for just the flagged sites, with live
 * progress. Powered by `GET /api/monitoring/portfolio/sync-health`.
 */
export default function SyncHealthBadge({ onSelectSite }) {
  const { user } = useAuth();
  const canBackfill = user?.role === "admin" || user?.role === "manager";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(null);   // null | backfill status object
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/monitoring/portfolio/sync-health?lookback_days=7");
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data: st } = await api.get("/monitoring/portfolio/backfill-status");
        setProgress(st);
        if (st.state === "finished" || st.state === "idle") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (st.state === "finished") {
            toast.success(`Gap backfill done · ${st.days_backfilled || 0} day(s), ${(st.total_written || 0).toLocaleString()} samples across ${st.sites_ok || 0} site(s)`);
          }
          await load();            // refresh health — gaps should clear
          setTimeout(() => setProgress(null), 4000);
        }
      } catch { /* transient — keep polling */ }
    }, 2000);
  };

  const backfillGaps = async () => {
    if (!data) return;
    const ids = data.sites.filter(s => s.status !== "ok").map(s => s.project_id);
    if (!ids.length) return;
    if (!window.confirm(`Backfill the last 30 days of missing data for ${ids.length} flagged site(s)? This runs in the background and may take a few minutes.`)) return;
    setStarting(true);
    try {
      const { data: r } = await api.post("/monitoring/portfolio/backfill-all-gaps", {
        lookback_days: 30, fill_gaps_only: true, project_ids: ids,
      });
      if (r.status === "started") {
        toast.success(`Backfilling ${r.total_sites} flagged site(s)…`);
        setProgress({ state: "running", total_sites: r.total_sites, sites_done: 0,
                      sites_ok: 0, sites_error: 0, days_backfilled: 0, total_written: 0 });
        startPolling();
      } else if (r.status === "already_running") {
        toast.info("A backfill is already in progress");
        setProgress(r.progress);
        startPolling();
      } else if (r.status === "no_configured_sites") {
        toast.warning("No flagged sites to backfill");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start backfill");
    } finally { setStarting(false); }
  };

  if (!data || data.monitored_sites === 0) return null;

  const gaps = data.sites_with_gaps;
  const healthy = gaps === 0;
  const problemSites = data.sites.filter(s => s.status !== "ok");
  const isRunning = progress?.state === "running";
  const pct = progress && progress.total_sites > 0
    ? Math.min(100, Math.round((progress.sites_done / progress.total_sites) * 100))
    : 0;

  const tone = healthy
    ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", icon: "text-emerald-600" }
    : data.down_sites > 0
      ? { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "text-red-600" }
      : { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-600" };

  return (
    <div className={`mb-6 border ${tone.border} ${tone.bg}`} data-testid="sync-health-badge">
      <div className="w-full flex items-center gap-3 px-4 py-3 flex-wrap sm:flex-nowrap">
        <button
          type="button"
          onClick={() => !healthy && setOpen(v => !v)}
          className={`flex-1 min-w-[220px] flex items-center gap-3 text-left ${healthy ? "cursor-default" : "cursor-pointer"}`}
          data-testid="sync-health-toggle"
        >
          {healthy
            ? <CheckCircle2 size={18} className={tone.icon} />
            : <AlertTriangle size={18} className={tone.icon} />}
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold ${tone.text}`} data-testid="sync-health-headline">
              {healthy
                ? `All ${data.monitored_sites} monitored site${data.monitored_sites === 1 ? "" : "s"} syncing cleanly this week`
                : `${gaps} site${gaps === 1 ? "" : "s"} with sync gaps this week`}
            </div>
            <div className="text-[11px] font-mono muted mt-0.5 flex items-center gap-1 flex-wrap">
              <Activity size={10} />
              Last {data.lookback_days} days · {data.healthy_sites}/{data.monitored_sites} healthy
              {data.down_sites > 0 && <span className={tone.text}>· {data.down_sites} down (no data all week)</span>}
              {data.total_missing_days > 0 && <span>· {data.total_missing_days} missing site-day{data.total_missing_days === 1 ? "" : "s"}</span>}
            </div>
          </div>
          {!healthy && (
            <span className={`shrink-0 ${tone.icon}`}>
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}
        </button>
        {!healthy && canBackfill && (
          <button
            type="button"
            onClick={backfillGaps}
            disabled={starting || isRunning}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-50 rounded-sm"
            data-testid="sync-health-backfill-btn"
            title="Fill the last 30 days of missing data for all flagged sites"
          >
            <Database size={12} className={isRunning ? "animate-pulse" : ""} />
            {isRunning ? "Backfilling…" : starting ? "Starting…" : "Backfill all gaps"}
          </button>
        )}
        <button
          type="button"
          onClick={() => load()}
          className="p-1.5 hover:bg-white/60 rounded-sm shrink-0"
          data-testid="sync-health-refresh"
          title="Re-check sync health"
        >
          <RefreshCw size={13} className={`${tone.icon} ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {progress && (
        <div className="px-4 pb-3" data-testid="sync-health-progress">
          <div className="flex items-center justify-between text-[11px] font-mono muted mb-1">
            <span>{isRunning ? "Backfilling flagged sites…" : "Backfill complete"}</span>
            <span>{progress.sites_done} / {progress.total_sites} · {(progress.total_written || 0).toLocaleString()} samples</span>
          </div>
          <div className="w-full h-2 bg-white/70 border border-white overflow-hidden rounded-sm">
            <div className="h-full transition-all"
                 style={{ width: `${pct}%`,
                          background: progress.state === "finished"
                            ? (progress.sites_error > 0 ? "var(--warm)" : "var(--ok, #10b981)")
                            : "var(--accent)" }}
                 data-testid="sync-health-progress-bar" />
          </div>
        </div>
      )}

      {open && !healthy && (
        <div className="border-t border-white/60 divide-y divide-white/50" data-testid="sync-health-list">
          {problemSites.map(s => (
            <button
              key={s.project_id}
              type="button"
              onClick={() => onSelectSite && onSelectSite(s.project_id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-white/50 transition-colors"
              data-testid={`sync-health-site-${s.project_id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-[11px] font-mono muted">
                  {s.data_source} · last synced {s.last_sync_date || "never"}
                </div>
              </div>
              <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm ${
                s.status === "down"
                  ? "text-red-700 bg-red-100 border-red-200"
                  : "text-amber-700 bg-amber-100 border-amber-200"}`}>
                {s.status === "down"
                  ? "No data"
                  : `${s.missing_days} gap day${s.missing_days === 1 ? "" : "s"}`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
